import { Badge, Button, Card, CardHeader, EmptyState, Table, formatMoney, useToast } from "@helmr/ui";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createCoupon, createItem, deleteCoupon, deleteItem, getCoupon, getItem, ITEM_KINDS, listAllItems, listCoupons, listItems, patchCoupon, patchItem,
  type CatalogueItem, type Coupon, type ItemKind, type ScopeFields } from "../../api/catalogue-admin";
import { getSettings, listBrands } from "../../api/settings";
import { ConfirmDrawer, FormSkeleton, Load, mapError, Page, TableSkeleton, useScope } from "./kit";
import { useBranchOptions } from "./SectionPages";
import { SectionForm, type CustomProps, type FieldDef } from "./SectionForm";
import s from "./settings.module.css";
import { section } from "./sections";
import { t } from "./strings";

const KIND_LABEL: Record<ItemKind, string> = { membership: "Membership", pt_pack: "PT pack", class_pack: "Class pack", service: "Service", product: "Product", fee: "Fee", combo: "Combo" };
const WITH_DURATION: readonly ItemKind[] = ["membership", "pt_pack", "class_pack"];
const WITH_SESSIONS: readonly ItemKind[] = ["pt_pack", "class_pack"];

/* ---------- scope: one branch, chosen branches, or a whole brand ---------- */

function ScopeEditor({ value, onChange, disabled }: CustomProps) {
  const sc = useScope();
  const v = value as ScopeFields;
  const branches = useBranchOptions();
  const brands = useQuery({ queryKey: sc.key("brands"), queryFn: () => listBrands(sc.client), retry: false });
  const set = (p: Partial<ScopeFields>) => onChange({ ...v, ...p });
  return (
    <div className={s.stack}>
      <select className={s.select} disabled={disabled} value={v.scopeMode} aria-label="Where it applies" onChange={(e) => {
        // Only the fields of the new mode survive: a brand left behind refuses a branch of another brand, with no brand picker on screen.
        const scopeMode = e.target.value as ScopeFields["scopeMode"];
        set({ scopeMode, branchIds: [], ...(scopeMode === "all" ? { homeBranchId: null } : { brandId: null }) });
      }}>
        <option value="one">One branch</option>
        <option value="chosen">Chosen branches</option>
        <option value="all">Every branch of a brand</option>
      </select>
      {v.scopeMode === "all" ? (
        <select className={s.select} disabled={disabled} value={v.brandId ?? ""} aria-label="Brand" onChange={(e) => set({ brandId: e.target.value || null, homeBranchId: null })}>
          <option value="">Choose a brand…</option>
          {(brands.data ?? []).filter((b) => b.archivedAt === null).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      ) : (
        <select className={s.select} disabled={disabled} value={v.homeBranchId ?? ""} aria-label={v.scopeMode === "one" ? "Branch" : "Home branch"} onChange={(e) => set({ homeBranchId: e.target.value || null })}>
          <option value="">Choose a branch…</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      )}
      {v.scopeMode === "chosen" ? (
        <div className={s.checks} role="group" aria-label="Also available at">
          {branches.filter((b) => b.id !== v.homeBranchId).map((b) => (
            <label key={b.id} className={s.check}><input type="checkbox" disabled={disabled} checked={v.branchIds.includes(b.id)} onChange={(e) => set({ branchIds: e.target.checked ? [...v.branchIds, b.id] : v.branchIds.filter((x) => x !== b.id) })} />{b.name}</label>
          ))}
        </div>
      ) : null}
    </div>
  );
}
const scopeOf = (x: Partial<ScopeFields>): ScopeFields => ({ scopeMode: x.scopeMode ?? "one", brandId: x.brandId ?? null, homeBranchId: x.homeBranchId ?? null, branchIds: x.branchIds ?? [] });
const scopeField = (): FieldDef => ({ key: "scope", path: [], label: "Where it applies", kind: "custom", full: true, spread: true, read: (d: ScopeFields) => scopeOf(d), render: (p) => <ScopeEditor {...p} /> });

/* ---------- catalogue list ---------- */

export function CataloguePage() {
  const sc = useScope();
  const [kind, setKind] = useState("");
  const { currency = "", locale = "en" } = sc.ctx.me?.business ?? {};
  const q = useInfiniteQuery({
    queryKey: sc.key("items", kind), initialPageParam: null as string | null, retry: false,
    queryFn: ({ pageParam }) => listItems(sc.client, { cursor: pageParam, kind: kind || undefined }),
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const canAdd = sc.ctx.has("catalogue.plans_add");
  return (
    <Page title={t.sections.catalogue.title} description={t.sections.catalogue.body} allow={section("catalogue")}
      actions={canAdd ? <Button variant="primary" as={Link} to="/settings/catalogue/new">Add item</Button> : undefined}>
      <Card aria-label="Catalogue items">
        <CardHeader title="Items" />
        <div className={s.tools}>
          <select className={s.select} aria-label="Filter by kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">All kinds</option>{ITEM_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
        </div>
        <Load query={q} what="the catalogue" skeleton={<TableSkeleton />}>
          {() => rows.length === 0
            ? <EmptyState compact icon="receipt" title="No plans or items yet" description="Add a membership, pack or service so you can sell it at the counter." action={canAdd ? <Button as={Link} to="/settings/catalogue/new">Add item</Button> : undefined} />
            : <Table label="Catalogue items" rows={rows} rowKey={(r) => r.id} minWidth={620} hasMore={q.hasNextPage} onLoadMore={() => void q.fetchNextPage()} loadingMore={q.isFetchingNextPage} summary={`${rows.length} shown`}
              columns={[
                { key: "n", header: "Name", cell: (r) => <Link to={`/settings/catalogue/${r.id}`}><span className={s.name} title={r.name}>{r.name}</span><span className={s.sub}>{r.sku ?? ""}</span></Link> },
                { key: "k", header: "Kind", cell: (r) => KIND_LABEL[r.kind] },
                { key: "p", header: "Price", align: "end", cell: (r) => `${formatMoney(r.price, currency, locale)}${r.priceIncludesTax ? " incl. tax" : ""}` },
                { key: "a", header: "Status", cell: (r) => <Badge tone={r.active ? "ok" : "neutral"}>{r.active ? "Active" : "Inactive"}</Badge> },
              ]} />}
        </Load>
      </Card>
    </Page>
  );
}

/* ---------- one item ---------- */

function ComponentsEditor({ value, onChange, disabled }: CustomProps) {
  const sc = useScope();
  // Its own cache entry: ("items", "") is the catalogue list's infinite query, a different shape under the same key would break it.
  const items = useQuery({ queryKey: sc.key("items", "all"), queryFn: () => listAllItems(sc.client), retry: false });
  const list = (value as { componentId: string; quantity: number }[] | null) ?? [];
  const options = (items.data ?? []).filter((i) => i.kind !== "combo");
  const set = (i: number, p: Partial<{ componentId: string; quantity: number }>) => onChange(list.map((x, j) => (j === i ? { ...x, ...p } : x)));
  return (
    <div className={s.stack}>
      {list.map((c, i) => (
        <div key={i} className={s.pair} style={{ gridTemplateColumns: "1fr 90px auto" }}>
          <select className={s.select} disabled={disabled} value={c.componentId} aria-label={`Component ${i + 1}`} onChange={(e) => set(i, { componentId: e.target.value })}>
            <option value="">Choose an item…</option>{options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <input className={s.input} disabled={disabled} inputMode="numeric" value={String(c.quantity)} aria-label={`Quantity ${i + 1}`} onChange={(e) => set(i, { quantity: Number(e.target.value.replace(/\D/g, "")) || 1 })} />
          <Button size="sm" disabled={disabled} onClick={() => onChange(list.filter((_, j) => j !== i))}>Remove</Button>
        </div>
      ))}
      {disabled ? null : <div><Button size="sm" onClick={() => onChange([...list, { componentId: "", quantity: 1 }])}>Add component</Button></div>}
    </div>
  );
}

function itemFields(kind: ItemKind, isNew: boolean): FieldDef[] {
  const f: FieldDef[] = [
    { key: "name", path: ["name"], label: "Name", kind: "text", full: true },
    { key: "description", path: ["description"], label: "Description", kind: "text", nullable: true, full: true },
    { key: "price", path: ["price"], label: "Price", kind: "money", hint: "In the business currency, as you would say it: 1,499." },
    { key: "priceIncludesTax", path: ["priceIncludesTax"], label: "Tax", kind: "bool", hint: "This price already includes tax" },
    { key: "taxRateBp", path: ["taxRateBp"], label: "Tax rate (%)", kind: "percent" },
    { key: "hsnSac", path: ["hsnSac"], label: "HSN / SAC code", kind: "text", nullable: true },
    { key: "sku", path: ["sku"], label: "SKU", kind: "text", nullable: true },
  ];
  if (WITH_DURATION.includes(kind)) f.push({ key: "durationDays", path: ["durationDays"], label: "Duration (days)", kind: "int" });
  if (WITH_SESSIONS.includes(kind)) f.push({ key: "sessionsTotal", path: ["sessionsTotal"], label: "Sessions in the pack", kind: "int" });
  if (kind === "membership") {
    f.push({ key: "nonHomeVisitLimit", path: ["nonHomeVisitLimit"], label: "Visits at other branches", kind: "int", nullable: true, hint: "Empty means no limit." });
    f.push({ key: "includesClasses", path: ["includesClasses"], label: "Classes", kind: "bool", hint: "Includes group classes" });
  }
  if (kind === "pt_pack") f.push({ key: "bookingStyle", path: ["bookingStyle"], label: "How it is booked", kind: "select", nullable: true,
    hint: "Fixed slot: pick the days and time once at sale. Daily: Monday to Saturday. Empty uses the business default.",
    options: [{ value: "flexible", label: "Session by session" }, { value: "fixed_slot", label: "Fixed weekly slot" }, { value: "daily", label: "Daily, Monday to Saturday" }] });
  if (kind === "fee") f.push({ key: "autoAdd", path: ["autoAdd"], label: "Billing", kind: "bool", hint: "Add to every bill automatically" });
  if (kind === "combo") f.push({ key: "components", path: ["components"], label: "What is in the combo", kind: "custom", full: true,
    // The API reads back name, kind and position but refuses them on write.
    read: (d: CatalogueItem) => d.components.map(({ componentId, quantity }) => ({ componentId, quantity })), render: (p) => <ComponentsEditor {...p} /> });
  f.push(scopeField());
  f.push({ key: "active", path: ["active"], label: "Sale", kind: "bool", hint: "Active: can be sold" });
  void isNew;
  return f;
}

export function ItemPage() {
  const { id = "" } = useParams();
  const isNew = id === "new";
  const sc = useScope();
  const nav = useNavigate();
  const [kind, setKind] = useState<ItemKind>("membership");
  const [del, setDel] = useState(false);
  const loaded = useQuery({ queryKey: sc.key(`item:${id}`), queryFn: () => getItem(sc.client, id), enabled: !isNew, retry: false });
  const settings = useQuery({ queryKey: sc.key("business"), queryFn: () => getSettings(sc.client), enabled: isNew, retry: false });
  const item = loaded.data;
  const effKind = isNew ? kind : (item?.kind ?? "membership");
  const fields = useMemo(() => itemFields(effKind, isNew), [effKind, isNew]);
  const canEdit = isNew ? sc.ctx.has("catalogue.plans_add") : sc.ctx.has("catalogue.plans_edit");
  const blank = useMemo<CatalogueItem>(() => ({
    id: "", kind: effKind, name: "", description: null, sku: null, nonHomeVisitLimit: null, price: 0,
    priceIncludesTax: settings.data?.tax.pricesIncludeTax ?? false, taxRateBp: settings.data?.tax.defaultRateBp ?? 0, hsnSac: null,
    durationDays: WITH_DURATION.includes(effKind) ? 30 : null, sessionsTotal: WITH_SESSIONS.includes(effKind) ? 10 : null,
    includesClasses: false, autoAdd: false, active: true, bookingStyle: null, components: [],
    scopeMode: "one", brandId: null, homeBranchId: sc.ctx.me?.branch?.id ?? null, branchIds: [], createdAt: 0, updatedAt: 0, archivedAt: null,
  }), [effKind, settings.data, sc.ctx.me?.branch?.id]);
  return (
    <Page title={isNew ? "Add item" : (item?.name ?? "Item")} description={t.sections.catalogue.body} allow={section("catalogue")}
      actions={!isNew && sc.ctx.has("catalogue.plans_delete") ? <Button onClick={() => setDel(true)}>Delete item</Button> : undefined}>
      {isNew ? (
        <Card><div className={s.tools} style={{ paddingTop: 12 }}>
          <label className={s.field} style={{ minWidth: 220 }}><span className={s.lab}>Kind of item</span>
            <select className={s.select} value={kind} onChange={(e) => setKind(e.target.value as ItemKind)}>{ITEM_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}</select></label>
        </div></Card>
      ) : null}
      {isNew && settings.isPending ? <Card><FormSkeleton /></Card> : <SectionForm key={`${id}:${effKind}`} title={isNew ? KIND_LABEL[kind] : "Item details"} what="the item" queryKey={`item:${id}${isNew ? `:${kind}` : ""}`} sendAll={isNew} blankForm={isNew}
        load={async (c) => (isNew ? blank : getItem(c, id))}
        save={(c, patch) => (isNew ? createItem(c, { ...patch, kind }) : patchItem(c, id, { ...patch, expectedUpdatedAt: item!.updatedAt }))}
        fields={fields} canEdit={canEdit}
        onSaved={() => { void sc.qc.invalidateQueries({ queryKey: sc.key("items") }); if (isNew) nav("/settings/catalogue"); }} />}
      {!isNew ? <ConfirmDeleteRow open={del} onClose={() => setDel(false)} name={item?.name ?? ""} what="item" run={() => deleteItem(sc.client, id)} listKey="items" back="/settings/catalogue" /> : null}
    </Page>
  );
}

function ConfirmDeleteRow({ open, onClose, name, what, run, listKey, back }: { open: boolean; onClose: () => void; name: string; what: string; run: () => Promise<unknown>; listKey: string; back: string }) {
  const sc = useScope();
  const nav = useNavigate();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: run,
    onSuccess: async () => { await sc.qc.invalidateQueries({ queryKey: sc.key(listKey) }); toast.show(`Deleted ${name}`); onClose(); nav(back); },
    onError: (e) => setError(mapError(e, []).form),
  });
  return <ConfirmDrawer open={open} onClose={() => { setError(null); onClose(); }} title={`Delete ${what}`} description={`${name} stops being sold. Bills already issued keep their own copy.`}
    confirmLabel={`Delete ${what}`} busyLabel="Deleting…" busy={m.isPending} error={error} onConfirm={() => m.mutate()} />;
}

/* ---------- coupons ---------- */

export function CouponsPage() {
  const sc = useScope();
  const { currency = "", locale = "en" } = sc.ctx.me?.business ?? {};
  const q = useInfiniteQuery({
    queryKey: sc.key("coupons"), initialPageParam: null as string | null, retry: false,
    queryFn: ({ pageParam }) => listCoupons(sc.client, { cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = q.data?.pages.flatMap((p) => p.coupons) ?? [];
  const canAdd = sc.ctx.has("catalogue.coupons_add");
  const off = (c: Coupon) => (c.kind === "percent" ? `${c.value / 100}% off` : `${formatMoney(c.value, currency, locale)} off`);
  return (
    <Page title={t.sections.coupons.title} description={t.sections.coupons.body} allow={section("coupons")}
      actions={canAdd ? <Button variant="primary" as={Link} to="/settings/coupons/new">Add coupon</Button> : undefined}>
      <Card aria-label="Coupons">
        <CardHeader title="Coupons" />
        <Load query={q} what="coupons" skeleton={<TableSkeleton />}>
          {() => rows.length === 0
            ? <EmptyState compact icon="receipt" title="No coupons yet" description="A coupon is a code the desk can apply to a bill." action={canAdd ? <Button as={Link} to="/settings/coupons/new">Add coupon</Button> : undefined} />
            : <Table label="Coupons" rows={rows} rowKey={(r) => r.id} hasMore={q.hasNextPage} onLoadMore={() => void q.fetchNextPage()} loadingMore={q.isFetchingNextPage} summary={`${rows.length} shown`}
              columns={[
                { key: "c", header: "Code", cell: (r) => <Link to={`/settings/coupons/${r.id}`}><span className={s.name} title={r.code}>{r.code}</span></Link> },
                { key: "v", header: "Discount", cell: off },
                { key: "w", header: "Valid", cell: (r) => (r.validFrom || r.validTo ? `${r.validFrom ?? "…"} to ${r.validTo ?? "…"}` : "Always") },
                { key: "a", header: "Status", cell: (r) => <Badge tone={r.active ? "ok" : "neutral"}>{r.active ? "Active" : "Inactive"}</Badge> },
              ]} />}
        </Load>
      </Card>
    </Page>
  );
}

function ItemKindsEditor({ value, onChange, disabled }: CustomProps) {
  const kinds = (value as ItemKind[] | null) ?? null;
  return (
    <div className={s.stack}>
      <label className={s.check}><input type="checkbox" disabled={disabled} checked={kinds === null} onChange={(e) => onChange(e.target.checked ? null : [...ITEM_KINDS])} />Applies to every kind of item</label>
      {kinds ? <div className={s.checks} role="group" aria-label="Item kinds">{ITEM_KINDS.map((k) => (
        <label key={k} className={s.check}><input type="checkbox" disabled={disabled} checked={kinds.includes(k)} onChange={(e) => onChange(e.target.checked ? [...kinds, k] : kinds.filter((x) => x !== k))} />{KIND_LABEL[k]}</label>
      ))}</div> : null}
    </div>
  );
}

export function CouponPage() {
  const { id = "" } = useParams();
  const isNew = id === "new";
  const sc = useScope();
  const nav = useNavigate();
  const [del, setDel] = useState(false);
  const loaded = useQuery({ queryKey: sc.key(`coupon:${id}`), queryFn: () => getCoupon(sc.client, id), enabled: !isNew, retry: false });
  const coupon = loaded.data;
  const [kind, setKind] = useState<"percent" | "fixed">("percent");
  const effKind = isNew ? kind : (coupon?.kind ?? "percent");
  const fields: FieldDef[] = useMemo(() => [
    { key: "code", path: ["code"], label: "Code", kind: "text", upper: true, hint: "2 to 32 letters, digits, - or _." },
    ...(isNew ? [] : []),
    effKind === "percent"
      ? { key: "value", path: ["value"], label: "Discount (%)", kind: "percent" as const }
      : { key: "value", path: ["value"], label: "Discount amount", kind: "money" as const },
    ...(effKind === "percent" ? [{ key: "maxDiscount", path: ["maxDiscount"], label: "Most it can take off", kind: "money" as const, nullable: true, hint: "Empty means no cap." }] : []),
    { key: "minPurchase", path: ["minPurchase"], label: "Minimum bill", kind: "money", nullable: true },
    { key: "validFrom", path: ["validFrom"], label: "Valid from", kind: "text", nullable: true, placeholder: "YYYY-MM-DD" },
    { key: "validTo", path: ["validTo"], label: "Valid until", kind: "text", nullable: true, placeholder: "YYYY-MM-DD" },
    { key: "maxRedemptions", path: ["maxRedemptions"], label: "Most uses overall", kind: "int", nullable: true },
    { key: "maxPerMember", path: ["maxPerMember"], label: "Most uses per member", kind: "int", nullable: true },
    { key: "itemKinds", path: ["itemKinds"], label: "Applies to", kind: "custom", full: true, render: (p) => <ItemKindsEditor {...p} /> },
    scopeField(),
    { key: "active", path: ["active"], label: "Status", kind: "bool", hint: "Active: the desk can apply it" },
  ] satisfies FieldDef[], [effKind, isNew]);
  const blank: Coupon = { id: "", code: "", kind, value: 0, maxDiscount: null, minPurchase: null, validFrom: null, validTo: null, maxRedemptions: null, maxPerMember: null, itemKinds: null, active: true,
    scopeMode: "one", brandId: null, homeBranchId: sc.ctx.me?.branch?.id ?? null, branchIds: [], createdAt: 0, updatedAt: 0, archivedAt: null };
  return (
    <Page title={isNew ? "Add coupon" : (coupon?.code ?? "Coupon")} description={t.sections.coupons.body} allow={section("coupons")}
      actions={!isNew && sc.ctx.has("catalogue.coupons_delete") ? <Button onClick={() => setDel(true)}>Delete coupon</Button> : undefined}>
      {isNew ? (
        <Card><div className={s.tools} style={{ paddingTop: 12 }}>
          <label className={s.field} style={{ minWidth: 220 }}><span className={s.lab}>Type of discount</span>
            <select className={s.select} value={kind} onChange={(e) => setKind(e.target.value as "percent" | "fixed")}><option value="percent">Percentage off</option><option value="fixed">Fixed amount off</option></select></label>
        </div></Card>
      ) : null}
      <SectionForm key={`${id}:${effKind}`} title={isNew ? "New coupon" : "Coupon details"} what="the coupon" queryKey={`coupon:${id}${isNew ? `:${kind}` : ""}`} sendAll={isNew} blankForm={isNew}
        load={async (c) => (isNew ? blank : getCoupon(c, id))}
        save={(c, patch) => (isNew ? createCoupon(c, { ...patch, kind }) : patchCoupon(c, id, { ...patch, expectedUpdatedAt: coupon!.updatedAt }))}
        fields={fields} canEdit={isNew ? sc.ctx.has("catalogue.coupons_add") : sc.ctx.has("catalogue.coupons_edit")}
        onSaved={() => { void sc.qc.invalidateQueries({ queryKey: sc.key("coupons") }); if (isNew) nav("/settings/coupons"); }} />
      {!isNew ? <ConfirmDeleteRow open={del} onClose={() => setDel(false)} name={coupon?.code ?? ""} what="coupon" run={() => deleteCoupon(sc.client, id)} listKey="coupons" back="/settings/coupons" /> : null}
    </Page>
  );
}
